import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';

import { getRegions } from '@luxuryescapes/lib-regions';

import { useGetLuxPlusPurchaseRestrictions } from '~/hooks/apis/offers/useGetLuxPlusPurchaseRestrictions';
import { useUpdateLuxPlusPurchaseRestrictions } from '~/hooks/apis/offers/useUpdateLuxPlusPurchaseRestrictions';

import type { LuxPlusPurchaseRestrictionRow } from '~/services/OffersService';

type RestrictionRow = {
  id: number;
  offerId: string;
  offerTitle: string;
  regionCode: string;
  regionName: string;
  restrictPurchaseToLuxPlusMembers: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function parseInitialPageSize(value: string | null): number {
  const parsed = Number(value);
  return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

function toRestrictionRow(row: LuxPlusPurchaseRestrictionRow, regionNameByCode: Map<string, string>): RestrictionRow {
  return {
    id: row.id,
    offerId: row.offerId,
    offerTitle: row.title ?? '',
    regionCode: row.regionCode,
    regionName: regionNameByCode.get(row.regionCode) ?? row.regionCode,
    restrictPurchaseToLuxPlusMembers: row.restrictPurchaseToLuxPlusMembers,
  };
}

function LuxPlusPurchaseRestrictionsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const { enqueueSnackbar } = useSnackbar();

  const regionOptions = useMemo(
    () =>
      getRegions()
        .map((region) => ({
          code: region.code,
          name: region.name,
        }))
        .sort((firstRegion, secondRegion) => firstRegion.name.localeCompare(secondRegion.name)),
    [],
  );

  const regionNameByCode = useMemo(
    () => new Map(regionOptions.map((region) => [region.code, region.name])),
    [regionOptions],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') ?? '';
  const initialPage = Math.max(0, (Number(searchParams.get('page')) || 1) - 1);
  const initialPageSize = parseInitialPageSize(searchParams.get('limit'));
  const regionParam = searchParams.get('region');
  const initialRegion =
    regionParam && regionOptions.some((region) => region.code === regionParam) ? regionParam : 'all';

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: initialPage,
    pageSize: initialPageSize,
  });
  const [selectedRegion, setSelectedRegion] = useState(initialRegion);
  const [regionInput, setRegionInput] = useState(initialRegion);
  const [offerSearchInput, setOfferSearchInput] = useState(initialSearch);
  const [offerSearch, setOfferSearch] = useState(initialSearch);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  // Pending checkbox edits keyed by row id, layered on top of the last fetched page
  // until "Apply" is clicked. Cleared whenever a fresh page of server data arrives.
  const [draftOverrides, setDraftOverrides] = useState<Map<number, boolean>>(() => new Map());

  const listParams = {
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    region: selectedRegion === 'all' ? undefined : selectedRegion,
    search: offerSearch || undefined,
  };

  const restrictionsQuery = useGetLuxPlusPurchaseRestrictions(listParams);
  const updateMutation = useUpdateLuxPlusPurchaseRestrictions();

  const baseRows = useMemo(
    () => (restrictionsQuery.data?.result.rows ?? []).map((row) => toRestrictionRow(row, regionNameByCode)),
    [restrictionsQuery.data, regionNameByCode],
  );
  const total = restrictionsQuery.data?.result.total ?? 0;

  useEffect(() => {
    setDraftOverrides(new Map());
    setSelectionModel([]);
    // Only reset when a new page of server data actually arrives, not on every render.
  }, [restrictionsQuery.data]);

  useEffect(() => {
    if (restrictionsQuery.error) {
      enqueueSnackbar(
        restrictionsQuery.error instanceof Error
          ? restrictionsQuery.error.message
          : 'Unable to load LuxPlus+ purchase restrictions.',
        { variant: 'error' },
      );
    }
  }, [restrictionsQuery.error, enqueueSnackbar]);

  const rows = useMemo(
    () =>
      baseRows.map((row) =>
        draftOverrides.has(row.id) ? { ...row, restrictPurchaseToLuxPlusMembers: draftOverrides.get(row.id) } : row,
      ),
    [baseRows, draftOverrides],
  );

  // Keep the URL in sync with the current search, page and rows-per-page so it can be
  // shared/bookmarked. setSearchParams identity changes on every URL change, so read it
  // from a ref to keep this effect depending only on the values that drive the URL.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedRegion !== 'all') {
      params.region = selectedRegion;
    }
    if (offerSearch) {
      params.search = offerSearch;
    }
    if (paginationModel.page > 0) {
      params.page = String(paginationModel.page + 1);
    }
    if (paginationModel.pageSize !== DEFAULT_PAGE_SIZE) {
      params.limit = String(paginationModel.pageSize);
    }
    setSearchParamsRef.current(params, { replace: true });
  }, [selectedRegion, offerSearch, paginationModel.page, paginationModel.pageSize]);

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(selectionModel);
    return rows.filter((row) => selectedIds.has(row.id));
  }, [rows, selectionModel]);

  // Toggling checkboxes only updates local state — nothing is sent to the backend
  // until "Apply" is clicked.
  const dirtyRows = useMemo(() => rows.filter((row) => draftOverrides.has(row.id)), [rows, draftOverrides]);
  const dirtyRowIds = useMemo(() => new Set(dirtyRows.map((row) => row.id)), [dirtyRows]);

  // An override is only kept while it differs from the server value, so toggling a row
  // back to its original state clears its "unsaved" flag instead of staying dirty.
  const setOverrideForRows = useCallback(
    (rowIds: Array<number>, nextValue: boolean) => {
      setDraftOverrides((current) => {
        const next = new Map(current);
        rowIds.forEach((rowId) => {
          const baseRow = baseRows.find((row) => row.id === rowId);
          if (baseRow?.restrictPurchaseToLuxPlusMembers === nextValue) {
            next.delete(rowId);
          } else {
            next.set(rowId, nextValue);
          }
        });
        return next;
      });
    },
    [baseRows],
  );

  const updateRowRestriction = useCallback(
    (rowId: number, restrictPurchaseToLuxPlusMembers: boolean) => {
      setOverrideForRows([rowId], restrictPurchaseToLuxPlusMembers);
    },
    [setOverrideForRows],
  );

  const updateSelectedRows = (restrictPurchaseToLuxPlusMembers: boolean) => {
    if (selectedRows.length === 0) {
      return;
    }

    setOverrideForRows(
      selectedRows.map((row) => row.id),
      restrictPurchaseToLuxPlusMembers,
    );
  };

  const resetChanges = () => {
    setDraftOverrides(new Map());
  };

  const applyChanges = async () => {
    if (dirtyRows.length === 0 || updateMutation.isPending) {
      return;
    }

    const changedCount = dirtyRows.length;
    const restrictedIds = dirtyRows.filter((row) => row.restrictPurchaseToLuxPlusMembers).map((row) => row.id);
    const unrestrictedIds = dirtyRows.filter((row) => !row.restrictPurchaseToLuxPlusMembers).map((row) => row.id);
    const offerIdById = new Map(rows.map((row) => [row.id, row.offerId]));

    try {
      // A single request/transaction covering every changed schedule (across all regions),
      // matched by id. This avoids firing one request per region.
      const response = await updateMutation.mutateAsync({
        updates: [
          { ids: restrictedIds, restrictPurchaseToLuxPlusMembers: true },
          { ids: unrestrictedIds, restrictPurchaseToLuxPlusMembers: false },
        ].filter((update) => update.ids.length > 0),
      });

      const missingOfferIds = response.result.missingIds.map((id) => offerIdById.get(id) ?? String(id));

      if (missingOfferIds.length > 0) {
        enqueueSnackbar(`Applied changes. Missing LuxPlus+ schedules for ${missingOfferIds.join(', ')}.`, {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(`Applied ${changedCount} schedule change${changedCount === 1 ? '' : 's'}.`, {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to apply purchase restriction changes.', {
        variant: 'error',
      });
    }
  };

  const frozen = restrictionsQuery.isFetching || updateMutation.isPending;
  const filteredRowCountLabel = restrictionsQuery.isFetching ? 'Loading…' : `${rows.length} of ${total} schedule rows`;

  const columns = useMemo<Array<GridColDef<RestrictionRow>>>(
    () => [
      {
        field: 'id',
        headerName: 'Entry ID',
        width: 110,
      },
      {
        field: 'offerId',
        headerName: 'Offer ID',
        width: 210,
        renderCell: ({ row }) => (
          <Link component={RouterLink} to={`/offers/${row.offerId}`} target="_blank">
            {row.offerId}
          </Link>
        ),
      },
      {
        field: 'offerTitle',
        headerName: 'Offer',
        flex: 0.75,
        minWidth: 180,
      },
      {
        field: 'regionCode',
        headerName: 'Region',
        width: 180,
        valueGetter: (_value, row) => `${row.regionName} (${row.regionCode})`,
      },
      {
        field: 'restrictPurchaseToLuxPlusMembers',
        headerName: 'Restrict purchase to LuxPlus+',
        width: 260,
        renderCell: ({ row, value }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={!!value}
              disabled={frozen}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => updateRowRestriction(row.id, event.target.checked)}
            />
            {dirtyRowIds.has(row.id) && (
              <Chip
                size="small"
                variant="outlined"
                label="unsaved"
                sx={{
                  height: 18,
                  fontSize: 11,
                  bgcolor: 'background.paper',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            )}
          </Stack>
        ),
      },
    ],
    [dirtyRowIds, frozen, updateRowRestriction],
  );

  // Region and offer-id filters are staged locally and only committed (triggering a
  // backend call + URL update) when "Search" is clicked / Enter is pressed.
  const applyFilters = () => {
    setSelectedRegion(regionInput);
    setOfferSearch(offerSearchInput);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectionModel([]);
  };

  const clearFilters = () => {
    setRegionInput('all');
    setSelectedRegion('all');
    setOfferSearchInput('');
    setOfferSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectionModel([]);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.25,
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: 1.2,
            background: `linear-gradient(135deg, ${themeColor}, ${alpha(themeColor, 0.7)})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          LuxPlus+ purchase restrictions
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Manage which hotel offer schedules are restricted to LuxPlus+ members.
        </Typography>
      </Paper>
      <Stack gap={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={regionInput}
                label="Region"
                disabled={frozen}
                onChange={(event) => setRegionInput(event.target.value)}
              >
                <MenuItem value="all">All regions</MenuItem>
                {regionOptions.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {region.name} ({region.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Offer IDs"
              value={offerSearchInput}
              disabled={frozen}
              onChange={(event) => setOfferSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyFilters();
                }
              }}
              placeholder="006RF00000S9HfqYAF, 006RF00000SRlUEYA1"
            />

            <Button variant="contained" onClick={applyFilters} disabled={frozen}>
              Search
            </Button>
            <Button variant="outlined" onClick={clearFilters} disabled={frozen}>
              Clear
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {filteredRowCountLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectionModel.length} selected, {dirtyRows.length} unsaved on this page
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <Button
                variant="outlined"
                onClick={() => setSelectionModel([])}
                disabled={selectionModel.length === 0 || frozen}
              >
                Clear selection
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={selectedRows.length === 0 || frozen}
                onClick={() => updateSelectedRows(true)}
              >
                Restricted to LuxPlus+ only
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={selectedRows.length === 0 || frozen}
                onClick={() => updateSelectedRows(false)}
              >
                Not restricted to LuxPlus+ only
              </Button>
            </Stack>
          </Stack>

          <DataGrid
            rows={rows}
            columns={columns}
            loading={frozen}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={setSelectionModel}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model);
              setSelectionModel([]);
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            getRowClassName={(params) => (dirtyRowIds.has(params.id as number) ? 'unsaved-row' : '')}
            sx={{
              '& .unsaved-row': {
                bgcolor: (theme) => theme.palette.action.hover,
              },
              '& .unsaved-row .MuiDataGrid-cell[data-field="restrictPurchaseToLuxPlusMembers"]': {
                borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
              },
            }}
            autoHeight
          />

          <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={dirtyRows.length === 0 || frozen} onClick={resetChanges}>
              Reset
            </Button>
            <Button variant="contained" disabled={dirtyRows.length === 0 || frozen} onClick={() => void applyChanges()}>
              {updateMutation.isPending ? <CircularProgress size={20} /> : 'Apply'}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

export default LuxPlusPurchaseRestrictionsPage;
